import type { Routes } from "@angular/router";
import { authGuard } from "./core/guards/auth/auth.guard";
import { privilegesGuard } from "./core/guards/privileges/privileges.guard";
import { UserPrivilegeState, UsersPrivilegeKey } from "./core/models/user";
import { AccessDenyComponent } from "./pages/access-deny/access-deny.component";
import { ProfileComponent } from "./pages/profile/profile.component";
import { LayoutComponent } from "./pages/layout/layout.component";
import { UserListComponent } from "./pages/users/user-list/user-list.component";

export const routes: Routes = [
	{
		path: "auth",
		loadChildren: () =>
			import("./pages/auth/auth.routes").then((m) => m.AUTH_ROUTES),
	},
	{
		path: "",
		component: LayoutComponent,
		canActivate: [authGuard],
		children: [
			{
				path: "",
				redirectTo: "dashboard",
				pathMatch: "full",
			},
			{
				path: "dashboard",
				loadChildren: () =>
					import("./pages/dashboard/dashboard.routes").then(
						(m) => m.DASHBOARD_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Dashboard,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "orders",
				loadChildren: () =>
					import("./pages/orders/orders.routes").then((m) => m.ORDERS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Orders,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "test-orders",
				loadChildren: () =>
					import("./pages/test-orders/orders.routes").then((m) => m.ORDERS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Orders_Test_Orders,
						value: UserPrivilegeState.Read_Only,
					},
				},
			},
			{
				path: "internal-orders",
				loadChildren: () =>
					import("./pages/internal-orders/orders.routes").then((m) => m.ORDERS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Orders_Internal_Ordering,
						value: UserPrivilegeState.Read_Only,
					},
				},
			},		
			{
				path: "catalog",
				loadChildren: () =>
					import("./pages/catalog/catalog.routes").then(
						(m) => m.CATALOG_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Catalog,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "users",
				loadChildren: () =>
					import("./pages/users/users.routes").then((m) => m.USERS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Users,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "users/:type",
				loadChildren: () =>
					import("./pages/users/users.routes").then((m) => m.USERS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Users,
						value: UserPrivilegeState.Active,
					},
				},
			},			
			{
				path: "accounts",
				loadChildren: () =>
					import("./pages/accounts/accounts.routes").then(
						(m) => m.ACCOUNTS_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Accounts,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "schedules",
				loadChildren: () =>
					import("./pages/schedules/schedules.routes").then(
						(m) => m.SCHEDULES_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Schedule,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "templates",
				loadChildren: () =>
					import("./pages/templates/templates.routes").then(
						(m) => m.TEMPLATES_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Templates,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "storefront",
				loadChildren: () =>
					import("./pages/storefront/storefront.routes").then(
						(m) => m.STOREFRONT_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Stores,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "promotions",
				loadChildren: () =>
					import("./pages/promotions/promotions.routes").then(
						(m) => m.PROMOTION_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Promotions,
						value: UserPrivilegeState.Active,
					},
				},
			},
			{
				path: "program-settings",
				loadChildren: () =>
					import("./pages/program-settings/program-settings.routes").then(
						(m) => m.PROGRAM_SETTINGS_ROUTES,
					),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Templates,
						value: UserPrivilegeState.Active,
					},
				}, //TODO update privilege
			},
			{
				path: "help-center",
				loadChildren: () =>
					import("./pages/helps/helps.routes").then((m) => m.HELPS_ROUTES),
				canActivate: [authGuard, privilegesGuard],
				data: {
					requiredPrivilege: {
						key: UsersPrivilegeKey.Help_Center,
						value: UserPrivilegeState.Active,
					},
				}, //TODO update privilege
			},

			{
				path: "unauthorized",
				component: AccessDenyComponent,
				pathMatch: "full",
				canActivate: [authGuard, privilegesGuard],
			},
			{
				path: "profile",
				component: ProfileComponent,
				pathMatch: "full",
				canActivate: [authGuard, privilegesGuard],
			},
			{
				path: "settings",
				loadChildren: () =>
					import("./pages/settings/settings.routes").then(
						(module) => module.SETTINGS_ROUTES,
					),
			},
			{
				path: "**",
				pathMatch: "full",
				loadChildren: () =>
					import("./pages/page-not-found/page-not-found.routes").then(
						(m) => m.PAGE_NO_FOUND_ROUTES,
					),
			},
		],
	},
];
